import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { ConnectorPinsContext } from '../context/context'
import { getRequest, postRequest } from '../../../../../services/base'
import RelatedConnectorsRow from './RelatedConnectorsRow'
import Button from '../../../../common/Button'
import { NavContext } from '../../../../../context/global_context'
import { notifyError } from '../../../../../utils/notifications'
import InlineCheckbox from '../../../../common/InlineCheckbox'
import { Empty, Flex, Form, List, Modal, Space, Typography } from 'antd'
import IconEndConnector from '../../../../icons/IconEndConnector'
import IconStartConnector from '../../../../icons/IconStartConnector'
import {
  CaretDownOutlined,
  CaretUpOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons'
import InputText from '../../../../common/InputText'
import useDebouncedValue from '../../../../../utils/common_function'
// import { prettyPrintFormData } from '../../../../../helpers/utils'

const MAPPING_TYPES = {
  START: 'start',
  END: 'end',
}

const SelectConnectorSection = () => {
  const intl = useIntl()
  const [state, dispatch] = useContext(ConnectorPinsContext)
  const [navState, navDispatch] = useContext(NavContext)

  const location = useLocation()
  let { vehicle } = useParams()

  const [searchParams, setSearchParams] = useSearchParams()
  const [relatedConnector, setRelatedConnector] = useState([])
  const [selectedConnectors, setSelectedConnectors] = useState([])
  const navigate = useNavigate()

  let selected_pins = searchParams.getAll('selected_pins')

  useEffect(() => {
    dispatch({
      type: 'set_pins_filter_data',
      selected_pins:
        state?.selected_pins?.length > 0 ? state?.selected_pins : selected_pins,
      search_old_levels: searchParams.get('search_old_levels')
        ? searchParams.get('search_old_levels')
        : false,
      query_type: searchParams.get('query_type')
        ? searchParams.get('query_type')
        : 'db_level',
      dtc_mapping_type: navState?.destination_only
        ? MAPPING_TYPES.END
        : searchParams.get('dtc_mapping_type')
          ? searchParams.get('dtc_mapping_type')
          : MAPPING_TYPES.START,
    })
    if (selected_pins?.length > 0) {
      getRelatedConnectors()
    }
  }, [location])

  const getRelatedConnectors = async () => {
    let circuit_ids = [],
      pins = []
    
    selected_pins.forEach((row, index) => {
      circuit_ids.push(row.split('**$$')[1].split('/'))
      pins.push(row.split('/')[0])
    })
    let res = await getRequest(
      `/api/dtcs/related_connectors/?${createSearchParams({
        circuit_ids: circuit_ids,
        search_old_levels: state?.search_old_levels,
        dtc_mapping_type: state?.dtc_mapping_type,
        connector_name: searchParams.get('connector_name'),
        vehicle_id: vehicle,
        pins: pins,
        is_dtc: true,
      })}`
    )
    
    // The length check below leaves a failure completely silent, so the failure
    // branch has to be explicit: base.js returns {status: false}, not a rejection.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'dtcs.connectorMapping.relatedConnectorsLoadError' }))
      return
    }
    if (res?.data?.length > 0) {
      setRelatedConnector(res?.data)
    }
  }

  const onConnectorSelect = (connector_id) => {
    let tmp_rel_connector = [...selectedConnectors]
    if (tmp_rel_connector.includes(connector_id)) {
      tmp_rel_connector = tmp_rel_connector.filter(function (item) {
        return item !== connector_id
      })
    } else {
      tmp_rel_connector.push(connector_id)
    }
    setSelectedConnectors(tmp_rel_connector)
  }

  const createDTCMapping = async () => {
    navDispatch({
      type: 'loader',
      data: { showLoader: true, caption: intl.formatMessage({ id: 'dtcs.connectorMapping.mappingInProgress' }) },
      notification: {},
    })

    let formData = new FormData()
    let dtcIds = searchParams.getAll('dtc_id')
    formData.append('dtc_mapping_type', state?.dtc_mapping_type)
    selectedConnectors.map((connector_id, index) => {
      formData.append('related_connector_ids', connector_id)
    })
    dtcIds.map((dtc_id, index) => {
      formData.append('dtc_id', dtc_id)
    })
    state?.selected_pins.map((pin, index) => {
      let circuit_ids = pin.split('**$$')[1].split('/')
      circuit_ids.map((circuit_id, cr_index) => {
        formData.append('circuit_ids', circuit_id)
      })
    })
    formData.append('create_dtc_circuits', state?.with_circuit_filters)
    formData.append('map_devices', state?.map_devices)
    formData.append('vehicle_id', vehicle)
    formData.append('source_connector', searchParams.get('connector_name'))
    formData.append('is_destination_only', navState?.destination_only)
    /* prettyPrintFormData(formData)
    return */
    let res = await postRequest(`/api/dtcs/create_mapping/`, formData)

    // Clear the loader FIRST and unconditionally: the 'loader' reducer case
    // overwrites `notification`, so clearing it afterwards wiped the toast.
    navDispatch({ type: 'loader', data: {}, notification: {} })

    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'dtcs.mappings.createFailed' }))
      return
    }

    // `res?.errors.map(...)` guarded `res` but not `errors`, so a failed request
    // threw "Cannot read properties of undefined" and took the page down.
    let notification = (res?.errors || [])
      .map((notify) => ({
        notification_type: 'error',
        msg: notify,
      }))
      .concat(
        (res?.success || []).map((notify) => ({
          notification_type: 'success',
          msg: notify,
        }))
      )
    navDispatch({
      type: 'notification',
      data: notification,
    })
    getDtcsDetails()
  }

  const getDtcsDetails = async () => {
    let res = await getRequest(
      `/api/dtcs/mappings/?${createSearchParams({
        dtc_id: searchParams.getAll('dtc_id'),
      })}&vehicle=${vehicle}`
    )
    // base.js returns {status: false} rather than rejecting; without this the
    // `res?.data?.length > 0` test below silently renders an empty mapping.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'dtcs.mappings.loadError' }))
      return
    }
    if (res?.data?.length > 0) {
      let destination_only = false
      if (res?.data?.length > 0) {
        res?.data.forEach((dtcs_obj) => {
          if (dtcs_obj.destination_only) {
            destination_only = true
          }
        })
      }

      navDispatch({
        type: 'header',
        data: {
          dtc_details: res.data,
          destination_only: destination_only,
        },
      })
    }
  }

  const [searchValue, setSearchValue] = useState(null)
  const [sortOrder, setSortOrder] = useState(null)
  const [modal, contextHolder] = Modal.useModal()
  const debouncedSearch = useDebouncedValue(searchValue, 500)

  const activeKey = searchParams.get('dtc_mapping_type')
  const connectorIcon = (activeKey === MAPPING_TYPES.END) ? (
    <IconStartConnector />
  ) : (
    <IconEndConnector />
  )
  const connectorType = (activeKey === MAPPING_TYPES.END) ? (intl.formatMessage({ id: 'dtcs.connectorMapping.startConnector' })) : (intl.formatMessage({ id: 'dtcs.connectorMapping.endConnector' }))
  const searchPlaceholder = intl.formatMessage({ id: 'dtcs.connectorMapping.searchConnectors' }, { type: (activeKey === MAPPING_TYPES.START) ? 'End' : 'Start' })
  const selectPinsInst = intl.formatMessage({ id: 'dtcs.connectorMapping.selectPinsInstruction' }, { type: (activeKey === MAPPING_TYPES.START) ? 'End' : 'Start' })
  const noSearchResults = intl.formatMessage({ id: 'dtcs.connectorMapping.noSearchResults' })

  const ListHeader = () => {
    return (
      <div className="pins-header">
        <Flex
          align="center"
          gap={8}
          className="sorting-column-header"
          onClick={toggleSort}
        >
          <Typography.Text className="semi-bold">{intl.formatMessage({ id: 'dtcs.connectorMapping.connectors' })}</Typography.Text>

          <Flex vertical gap={0} className="sort-icon">
            <CaretUpOutlined className={sortOrder === 'asc' ? 'active' : ''} />
            <CaretDownOutlined
              className={sortOrder === 'desc' ? 'active' : ''}
            />
          </Flex>
        </Flex>
      </div>
    )
  }

  /* Filter "Related Connectors" based on search term */
  const filterRelatedConnectors = useCallback((data, search) => {
    if (!search) return data
    
    return data
      .map((connector) => {
        const content = `${connector.name} ${connector.description}`
        const match = content.toLowerCase().includes(search.toLowerCase())

        if (match) return connector

        return null
      })
      .filter(Boolean)
  }, [])

  /* Update "filteredConnectorData" used to cerate "Connectors List" when "debouncedSearch" value changes */
  const filteredConnectorData = useMemo(() => {
    return filterRelatedConnectors(relatedConnector, debouncedSearch)
  }, [relatedConnector, debouncedSearch, filterRelatedConnectors])

  // Sort Pins
  const sortedData = useMemo(() => {
    if (!sortOrder) return filteredConnectorData
    return [...filteredConnectorData].sort((a, b) => {
      return sortOrder === 'asc'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name)
    })
  }, [filteredConnectorData, sortOrder])

  const toggleSort = () => {
    setSortOrder((prev) => {
      switch (prev) {
        case null:
          return 'asc'
        case 'asc':
          return 'desc'
        default:
          return null
      }
    })
  }

  const showNoConnectorsSelectedError = () => {
    modal.info({
      title: intl.formatMessage({ id: 'dtcs.connectorMapping.errorTitle' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({ id: 'dtcs.connectorMapping.selectAtLeastOne' }),
      centered: true,
      maskClosable: true,
    })
  }

  const confirmDTCMapping = (id, name) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'dtcs.connectorMapping.confirmTitle' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({ id: 'dtcs.connectorMapping.confirmContent' }),
      centered: true,
      maskClosable: true,
      onOk: () => {
        createDTCMapping()
      }
    })
  }

  return (
    <>
      <Typography.Title level={5} className="col-header">
        <Typography.Text className="icon non-interactive">
          {connectorIcon}
        </Typography.Text>
        <Typography.Text className="title non-interactive">
          {connectorType}
        </Typography.Text>
      </Typography.Title>

      <Form name="select-connector-form" className="col-body">
        {!navState?.destination_only && (
          <>
            {relatedConnector && relatedConnector?.length > 1 && 
              <InputText
                type="search"
                name="search_connector"
                label={searchPlaceholder}
                placeholder={searchPlaceholder}
                value={searchValue}
                hideLabel={true}
                isRequired={false}
                disabled={relatedConnector.length < 2 ? true : false}
                onChange={(value) => {
                  setSearchValue(value)
                }}
              />
            }
            <Flex vertical justify="space-between" className="pins-section">
              <ListHeader />

              <div className="pins-list-container">
                <List
                  bordered
                  dataSource={sortedData}
                  className="pins-list"
                  renderItem={(conn_row, index) => {
                    return (
                      <List.Item data-testid="dtc-related-connector">
                        <RelatedConnectorsRow
                          {...conn_row}
                          key={`related-connector-row-${index}`}
                          onConnectorSelect={(connector_id) => {
                            onConnectorSelect(connector_id)
                          }}
                          selectedConnectors={selectedConnectors}
                        />
                      </List.Item>
                    )
                  }}
                  locale={{
                    emptyText: (
                      <Empty
                        description={
                          searchValue ? noSearchResults : selectPinsInst
                        }
                      />
                    ),
                  }}
                />
              </div>
            </Flex>

            <InlineCheckbox
              name={'with_circuit_filters'}
              label={intl.formatMessage({ id: 'dtcs.connectorMapping.withCircuitFilters' })}
              checked={state?.with_circuit_filters}
              disabled={relatedConnector.length === 0 ? true : false}
              onChange={(e) => {
                dispatch({ type: 'with_circuit_filters', data: e })
              }}
            />
          </>
        )}

        <InlineCheckbox
          name={'map_devices'}
          label={intl.formatMessage({ id: 'dtcs.connectorMapping.mapDevices' })}
          checked={state?.map_devices}
          disabled={
            !navState?.destination_only && relatedConnector.length === 0
              ? true
              : false
          }
          onChange={(e) => {
            dispatch({ type: 'map_devices', data: e })
          }}
        />

        <Flex gap={16}>
          <Button
            type="primary"
            htmlType="submit"
            style={{width: 'stretch'}}
            disabled={
              !navState?.destination_only && relatedConnector.length === 0
                ? true
                : false
            }
            onClick={(e) => {
              const valid = selectedConnectors?.length > 0 || navState?.destination_only
              valid ? confirmDTCMapping() : showNoConnectorsSelectedError()
            }}
            testId="dtc-add-mappings"
          >
            {intl.formatMessage({ id: 'dtcs.connectorMapping.addMappings' })}
          </Button>

          <Button
            style={{width: 'stretch'}}
            onClick={(e) => navigate(`/vehicles/${vehicle}/dtcs/listing`)}
          >
            {intl.formatMessage({ id: 'forms.cancel' })}
          </Button>
        </Flex>
      </Form>

      {contextHolder}
    </>
  )
}

export default SelectConnectorSection
